import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Alert,
  CircularProgress,
  LinearProgress,
  Checkbox,
} from '@mui/material';
import { Refresh, PlayArrow, Download } from '@mui/icons-material';
import { useAuthV2 } from '../../../contexts/AuthContextV2';
import { BRAND_COLORS } from '../../../styles/theme';
import { useTranslation } from '../../../i18n';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import { formatDateTimeISO } from '../../../utils/dateUtils';
import { useCurrentExtractionJobQuery, useExtractionJobStatusQuery } from '../adminTools.queries';
import {
  useStartExtractionMutation,
  useDownloadExtractionLogMutation,
} from '../adminTools.mutations';
import type { ExtractionJobResult } from '../../../types/api';

interface MetadataExtractionCardProps {
  /**
   * Passed in (not recomputed) so the extraction queries' `enabled` flags are
   * correct on the card's first render — see parent AdminToolsPage.
   */
  isXerAdmin: boolean;
}

/**
 * Metadata re-extraction admin tool. Owns its extraction state, resume/poll
 * effects, and start/download handlers; consumes the extraction queries and
 * mutations internally.
 */
export const MetadataExtractionCard: React.FC<MetadataExtractionCardProps> = ({ isXerAdmin }) => {
  const { profile } = useAuthV2();
  const { showError } = useErrorHandler();
  const { t } = useTranslation();

  const [reExtractionResult, setReExtractionResult] = useState<ExtractionJobResult | null>(null);
  const [reExtractionError, setReExtractionError] = useState('');
  const [fileProgress, setFileProgress] = useState({ current: 0, total: 0 });
  const [isJobRunning, setIsJobRunning] = useState(false);
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [currentJobInfo, setCurrentJobInfo] = useState<{
    started_by?: string;
    started_at?: string;
  } | null>(null);
  const [globalScope, setGlobalScope] = useState(false);

  // TanStack Query hooks — extraction (XerAdmin-only; disabled for regular org admins)
  const currentExtractionJobQuery = useCurrentExtractionJobQuery(isXerAdmin);
  const jobStatusQuery = useExtractionJobStatusQuery(currentJobId, isJobRunning ? 5000 : false);
  const startExtractionMutation = useStartExtractionMutation();
  const downloadLogMutation = useDownloadExtractionLogMutation();

  // React to current extraction job query on page load: resume polling if a job is running.
  useEffect(() => {
    const data = currentExtractionJobQuery.data;
    if (!data) return;
    if (data.has_current_job && data.job) {
      const job = data.job;
      if (job.status === 'running' || job.status === 'started') {
        setCurrentJobId(job.job_id);
        setIsJobRunning(true);
        setReExtractionResult(null);
        setReExtractionError('');
        setFileProgress({
          current: job.progress?.current || 0,
          total: job.progress?.total || 0,
        });
        setCurrentJobInfo({
          started_by: job.started_by,
          started_at: job.started_at,
        });
      } else if (job.status === 'completed') {
        setCurrentJobId(job.job_id);
        setIsJobRunning(false);
      }
    }
  }, [currentExtractionJobQuery.data]);

  // React to polled job status updates.
  useEffect(() => {
    const statusResult = jobStatusQuery.data;
    if (!statusResult) return;

    setFileProgress({
      current: statusResult.progress?.current || 0,
      total: statusResult.progress?.total || 0,
    });

    if (statusResult.status === 'completed') {
      const result = statusResult.result;
      if (result) {
        setReExtractionResult({
          success: result.success,
          message: result.message,
          total_files: result.total_files,
          processed_files: result.processed_files,
          failed_files: result.failed_files,
          failed_count: result.failed_count,
          duration_seconds: result.duration_seconds,
          log_file: result.log_file,
          s3_modifications: result.s3_modifications,
        });
      }
      setIsJobRunning(false);
    } else if (statusResult.status === 'failed') {
      setReExtractionError(statusResult.error_message || 'Metadata re-extraction failed');
      setIsJobRunning(false);
    }
  }, [jobStatusQuery.data]);

  // React to job status polling errors.
  useEffect(() => {
    if (jobStatusQuery.isError && isJobRunning) {
      setReExtractionError('Failed to get job status. The job may still be running.');
      setIsJobRunning(false);
    }
  }, [jobStatusQuery.isError, isJobRunning]);

  const handleRerunMetadataExtraction = async () => {
    if (isJobRunning) return;

    setReExtractionResult(null);
    setReExtractionError('');
    setFileProgress({ current: 0, total: 0 });

    try {
      const startResult = await startExtractionMutation.mutateAsync(globalScope);

      if (startResult.success) {
        setCurrentJobId(startResult.job_id);
        setCurrentJobInfo({
          started_by: profile?.email || 'Unknown',
          started_at: new Date().toISOString(),
        });
        setIsJobRunning(true);
      } else {
        throw new Error(startResult.message || 'Failed to start metadata re-extraction job');
      }
    } catch (error) {
      showError(error, 'Failed to start metadata re-extraction');
      setReExtractionError('Failed to start job');
    }
  };

  const handleDownloadLog = async (jobId: string) => {
    if (!jobId) return;

    try {
      const logBlob = await downloadLogMutation.mutateAsync(jobId);

      const url = window.URL.createObjectURL(logBlob);
      const link = document.createElement('a');
      link.href = url;

      const now = new Date();
      const timestamp = now.toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.download = `metadata-extraction-log-${timestamp}.log`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showError(error, 'Failed to download log file');
      setReExtractionError('Log download failed');
    }
  };

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Refresh sx={{ mr: 1, color: BRAND_COLORS.forestGreen.main }} />
          <Typography variant="h6" component="h2">
            {t('adminToolsPage.metadataExtraction.title')}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" paragraph>
          {t('adminToolsPage.metadataExtraction.description')}
        </Typography>

        <Box sx={{ mt: 1, mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('adminToolsPage.metadataExtraction.processingScope')}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              icon={<Checkbox checked={!globalScope} size="small" />}
              label={t('adminToolsPage.metadataExtraction.xerOnly')}
              color={!globalScope ? 'primary' : 'default'}
              variant={!globalScope ? 'filled' : 'outlined'}
              clickable
              disabled={isJobRunning}
              onClick={() => setGlobalScope(false)}
              sx={{
                '& .MuiChip-icon': {
                  color: !globalScope ? 'white' : 'inherit',
                },
              }}
            />

            <Chip
              icon={<Checkbox checked={globalScope} size="small" />}
              label={t('adminToolsPage.metadataExtraction.allOrganizations')}
              color={globalScope ? 'error' : 'default'}
              variant={globalScope ? 'filled' : 'outlined'}
              clickable
              disabled={isJobRunning}
              onClick={() => setGlobalScope(true)}
              sx={{
                '& .MuiChip-icon': {
                  color: globalScope ? 'white' : 'inherit',
                },
              }}
            />
          </Box>
        </Box>

        <Box sx={{ mt: 1 }}>
          {isJobRunning && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  🔄{' '}
                  {t('adminToolsPage.metadataExtraction.extractingFiles', {
                    current: fileProgress.current,
                    total: fileProgress.total,
                  })}
                </Typography>
              </Box>

              {currentJobInfo && (
                <Typography variant="body2" sx={{ mb: 1, opacity: 0.7, fontSize: '0.8rem' }}>
                  👤{' '}
                  {t('adminToolsPage.metadataExtraction.startedBy', {
                    user: currentJobInfo.started_by || 'Unknown',
                    time: currentJobInfo.started_at
                      ? formatDateTimeISO(currentJobInfo.started_at)
                      : 'Unknown',
                  })}
                </Typography>
              )}

              <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>
                {t('adminToolsPage.metadataExtraction.processingNote')}
              </Typography>
              <LinearProgress variant="indeterminate" sx={{ mb: 1 }} />
              <Typography variant="caption" color="text.secondary">
                {t('adminToolsPage.metadataExtraction.processingInProgress')}
              </Typography>
            </Box>
          )}

          {reExtractionResult && !isJobRunning && (
            <Alert severity={reExtractionResult.success ? 'success' : 'error'} sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {reExtractionResult.success
                  ? `✅ ${t('adminToolsPage.metadataExtraction.successMessage', {
                      processed: reExtractionResult.processed_files,
                      total: reExtractionResult.total_files,
                    })}`
                  : `❌ ${t('adminToolsPage.metadataExtraction.failedMessage', {
                      message: reExtractionResult.message || 'Metadata re-extraction failed',
                    })}`}
              </Typography>

              {reExtractionResult.success && (
                <>
                  {reExtractionResult.failed_count > 0 && (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      ⚠️ {reExtractionResult.failed_count} files failed to process
                    </Typography>
                  )}

                  {reExtractionResult.duration_seconds && (
                    <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.8 }}>
                      🕒 Completed in {Math.round(reExtractionResult.duration_seconds)} seconds
                    </Typography>
                  )}

                  {reExtractionResult.s3_modifications && (
                    <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.8 }}>
                      📁 Modified {reExtractionResult.s3_modifications.files_modified} S3 files with
                      enhanced CSV data
                    </Typography>
                  )}

                  {reExtractionResult.log_file && currentJobId && (
                    <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        📋 Detailed log:
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => handleDownloadLog(currentJobId)}
                        startIcon={<Download />}
                        sx={{
                          minHeight: 'auto',
                          padding: '2px 6px',
                          fontSize: '0.75rem',
                          textTransform: 'none',
                          color: BRAND_COLORS.forestGreen.main,
                          '&:hover': {
                            backgroundColor: `${BRAND_COLORS.forestGreen.main}10`,
                          },
                        }}
                      >
                        Download
                      </Button>
                    </Box>
                  )}
                </>
              )}

              {reExtractionResult.failed_files && reExtractionResult.failed_files.length > 0 && (
                <Box sx={{ mt: 1, maxHeight: 100, overflowY: 'auto' }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                    Failed files:
                  </Typography>
                  {reExtractionResult.failed_files
                    .slice(0, 3)
                    .map((failedFile: { file: string; error: string }, index: number) => (
                      <Typography key={index} variant="caption" sx={{ display: 'block', ml: 1 }}>
                        • {failedFile.file.split('/').pop()}: {failedFile.error}
                      </Typography>
                    ))}
                  {reExtractionResult.failed_files.length > 3 && (
                    <Typography variant="caption" sx={{ ml: 1, fontStyle: 'italic' }}>
                      ... and {reExtractionResult.failed_files.length - 3} more
                    </Typography>
                  )}
                </Box>
              )}
            </Alert>
          )}

          {reExtractionError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              <Typography variant="body2">{reExtractionError}</Typography>
            </Alert>
          )}
        </Box>
      </CardContent>

      <CardActions sx={{ p: 2, pt: 0 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={handleRerunMetadataExtraction}
          disabled={isJobRunning}
          startIcon={isJobRunning ? <CircularProgress size={16} /> : <PlayArrow />}
          sx={{
            backgroundColor: BRAND_COLORS.forestGreen.main,
            '&:hover': {
              backgroundColor: BRAND_COLORS.forestGreen.main,
              opacity: 0.8,
            },
          }}
        >
          {isJobRunning
            ? currentJobInfo
              ? t('adminToolsPage.metadataExtraction.jobRunning')
              : t('adminToolsPage.metadataExtraction.processing')
            : globalScope
              ? t('adminToolsPage.metadataExtraction.startGlobalReExtraction')
              : t('adminToolsPage.metadataExtraction.startReExtraction')}
        </Button>
      </CardActions>
    </Card>
  );
};
